import { ApiResponse, Invoice } from '../../types';
import { envConfig } from '../../config/env';
import apiClient from '../api/client';
import { delay } from '../mock/delay';
import { filterMockInvoices, findMockInvoiceById } from '../mock/invoices';

interface InvoiceListResponse {
  success: boolean;
  data: Invoice[];
  meta: {
    page: number;
    pageSize: number;
    count: number;
    next: string | null;
    previous: string | null;
  };
}

export const invoiceService = {
  getList: async (filters?: {
    invoiceNumber?: string;
    patientName?: string;
    service?: string;
    taxCode?: string;
    phone?: string;
    customerName?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }): Promise<InvoiceListResponse> => {
    if (envConfig.USE_INVOICE_MOCK) {
      await delay(250);
      const { data, meta } = filterMockInvoices(filters || {});
      return { success: true, data, meta };
    }

    const params: Record<string, any> = {};

    if (filters?.invoiceNumber) {
      params.invoice_number = filters.invoiceNumber;
    }
    if (filters?.patientName) {
      params.customer_name = filters.patientName;
    }
    if (filters?.customerName) {
      params.customer_name = filters.customerName;
    }
    if (filters?.service) {
      params.service_name = filters.service;
    }
    if (filters?.taxCode) {
      params.customer_tax_code = filters.taxCode;
    }
    if (filters?.phone) {
      params.customer_phone = filters.phone;
    }
    if (filters?.startDate) {
      params.start_date = filters.startDate;
    }
    if (filters?.endDate) {
      params.end_date = filters.endDate;
    }
    if (filters?.page) {
      params.page = filters.page;
    }
    if (filters?.pageSize) {
      params.page_size = filters.pageSize;
    }

    const response = await apiClient.get<InvoiceListResponse>('/invoices/', { params });
    return response.data;
  },

  getDetail: async (id: number): Promise<ApiResponse<Invoice>> => {
    if (envConfig.USE_INVOICE_MOCK) {
      await delay(200);
      const inv = findMockInvoiceById(id);
      if (!inv) {
        throw new Error(`Không tìm thấy hóa đơn #${id}`);
      }
      return { success: true, data: inv };
    }
    const response = await apiClient.get<ApiResponse<Invoice>>(`/invoices/${id}/`);
    return response.data;
  },

  importExcel: async (file: File): Promise<ApiResponse<{ imported?: number; errors?: unknown[] }>> => {
    if (envConfig.USE_INVOICE_MOCK) {
      await delay(600);
      void file;
      return { success: true, data: { imported: 1, errors: [] } };
    }
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<ApiResponse<{ imported?: number; errors?: unknown[] }>>(
      '/invoices/import-excel/',
      formData
    );
    return response.data;
  },

  downloadSampleInvoiceExcel: async (): Promise<Blob> => {
    if (envConfig.USE_INVOICE_MOCK) {
      await delay(200);
      const csv =
        'Mã KH,Tên,MST,Điện thoại,Dịch vụ,Số tiền\n512345678,NGUYỄN VĂN A,07912345678,0900000000,Nước SH,133400\n';
      return new Blob([csv], { type: 'text/csv;charset=utf-8' });
    }
    const response = await apiClient.get('/documents/sample-invoice-excel/', {
      responseType: 'blob',
    });
    return response.data;
  },

  exportInvoice: async (id: number): Promise<Blob> => {
    if (envConfig.USE_INVOICE_MOCK) {
      await delay(400);
      const text = `%PDF-mock hóa đơn #${id} — chỉ dữ liệu giả lúc USE_INVOICE_MOCK`;
      return new Blob([text], { type: 'application/pdf' });
    }
    const response = await apiClient.post(`/invoices/${id}/export/`, {}, { responseType: 'blob' });
    return response.data;
  },

  exportBulk: async (payload: {
    ids?: number[];
    filters?: {
      taxCode?: string;
      customerName?: string;
      phone?: string;
      startDate?: string;
      endDate?: string;
    };
    exportAllMatching?: boolean;
  }): Promise<Blob> => {
    if (envConfig.USE_INVOICE_MOCK) {
      await delay(500);
      const text = `ZIP-mock xuất hàng loạt — ${JSON.stringify(payload)}`;
      return new Blob([text], { type: 'application/zip' });
    }
    const response = await apiClient.post('/invoices/export-bulk/', payload, { responseType: 'blob' });
    return response.data;
  },
};

